import React, { useCallback, useEffect, useState } from 'react';
import {
  applyUpdate, fetchLatestRelease, fetchUpdateState, isNewerVersion, startUpdateDownload,
  type DesktopRelease, type UpdateState,
} from '../utils/desktopWindows';

/**
 * 「软件更新」面板（只在桌面端渲染，见 `APISettingsModal`）。
 *
 * ## 为什么是这样一套流程
 *
 * 用户要的是"**一个 exe** 就搞定"：程序本身就是一个 portable exe（下载即可运行、可以直接分享），
 * 所以更新就是"换掉这一个文件"，不该产生需要一起分发的文件夹。
 *
 * 检查由页面直接 `fetch('/api/desktop/latest')`（走既有代理，和业务请求同一条路）；
 * **下载与替换交给宿主**（Rust）—— 浏览器侧没有权限写自己的可执行文件。
 * 替换过程：下载到临时目录 → 校验 sha256 → 启动新版本 → 新版本把旧文件改名成 `.old`
 * 并把自身放回原位 → 重新启动（详见 `src-tauri/src/updater.rs`）。
 */
export function UpdateSetting() {
  const [current, setCurrent] = useState('');
  const [latest, setLatest] = useState<DesktopRelease | null>(null);
  const [state, setState] = useState<UpdateState>({ state: 'idle' });
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState('');

  // 当前版本：本地服务的诊断接口（它读的是编译进 exe 的版本号）
  useEffect(() => {
    void (async () => {
      try {
        const res = await fetch('/__xts/health');
        const data = await res.json() as { version?: string };
        setCurrent(data.version ?? '');
      } catch {
        /* 拿不到就只显示"未知"，不影响更新本身 */
      }
    })();
  }, []);

  // 下载中：轮询进度（宿主在后台线程里下载）
  useEffect(() => {
    if (state.state !== 'downloading') return;
    const timer = window.setInterval(() => {
      void (async () => {
        const next = await fetchUpdateState();
        if (next) setState(next);
      })();
    }, 500);
    return () => { window.clearInterval(timer); };
  }, [state.state]);

  const check = useCallback(async () => {
    setChecking(true);
    setMessage('');
    setLatest(null);
    const release = await fetchLatestRelease();
    setChecking(false);
    if (!release) {
      setMessage('服务器上还没有发布过桌面端版本（或暂时连不上后端）');
      return;
    }
    setLatest(release);
    setMessage(isNewerVersion(release.version, current) ? '' : '已经是最新版本');
  }, [current]);

  const download = useCallback(async () => {
    setMessage('');
    // 先乐观置为"下载中"，请求回来后再以宿主为准（见下面的说明）
    const preparing: UpdateState = { state: 'downloading', version: latest?.version ?? '', received: 0, total: 0, percent: 0 };
    setState(preparing);
    const next = await startUpdateDownload();
    /**
     * ⚠️ 不能只信返回值：宿主那边置状态与返回之间有时间差（线程还要先拉清单），
     * 若它返回 `idle` 而我们照抄，**轮询就不会启动** —— 用户看到的是
     * "第一次点『下载更新』毫无反应，再点一次才开始"（实测反馈）。
     * 宿主侧已改成同步置为"下载中"，这里再兜一层：只要不是明确失败/就绪，就当作在下载。
     */
    if (next && (next.state === 'failed' || next.state === 'ready')) {
      setState(next);
    } else if (next && next.state === 'downloading') {
      setState(next);
    } else {
      setState(preparing);
    }
  }, [latest]);

  const restart = useCallback(async () => {
    setMessage('正在重启到新版本…');
    const result = await applyUpdate();
    // 成功的话这里通常等不到后续 —— 宿主几百毫秒后就退出了
    if (!result.restarting) setMessage(result.error ?? '更新失败，请手动下载最新版本');
  }, []);

  const hasUpdate = Boolean(latest) && isNewerVersion(latest!.version, current);
  const labelStyle: React.CSSProperties = { fontSize: 13, color: 'var(--text-secondary)' };
  const primaryBtn: React.CSSProperties = {
    padding: '6px 16px', border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, fontWeight: 500, backgroundColor: 'var(--primary-color)', color: '#fff',
  };
  const ghostBtn: React.CSSProperties = {
    padding: '6px 16px', border: '1px solid var(--border-color)', borderRadius: 6,
    cursor: 'pointer', fontSize: 13, fontWeight: 500, background: 'transparent', color: 'var(--text-color)',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={labelStyle}>
        当前版本 <b style={{ color: 'var(--text-color)' }}>{current || '未知'}</b>
        {hasUpdate && (
          <> · 有新版本 <b style={{ color: 'var(--primary-color)' }}>{latest!.version}</b></>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button style={ghostBtn} onClick={() => void check()} disabled={checking}>
          {checking ? '检查中…' : '检查更新'}
        </button>
        {hasUpdate && state.state !== 'ready' && state.state !== 'downloading' && (
          <button style={primaryBtn} onClick={() => void download()}>下载更新</button>
        )}
        {state.state === 'ready' && (
          <button style={primaryBtn} onClick={() => void restart()}>立即重启更新</button>
        )}
      </div>

      {state.state === 'downloading' && (
        <div style={labelStyle}>
          {/* 版本号还没拿到（宿主正在拉清单）时给一句"准备中"，别显示成"正在下载 ：" */}
          {state.version ? `正在下载 ${state.version}` : '正在准备下载'}：
          {state.total > 0 ? `${state.percent}%` : (state.received > 0 ? `${Math.round(state.received / 1024)} KB` : '')}
          {/* 进度条：宽度按百分比，颜色跟随主题 */}
          <div style={{ marginTop: 6, height: 4, borderRadius: 2, background: 'var(--hover-bg)', overflow: 'hidden' }}>
            <div style={{
              width: `${state.total > 0 ? state.percent : 5}%`, height: '100%',
              background: 'var(--primary-color)', transition: 'width 0.2s',
            }} />
          </div>
        </div>
      )}

      {state.state === 'failed' && <div style={{ ...labelStyle, color: 'var(--danger-color, #c84b4b)' }}>{state.message}</div>}
      {message && <div style={labelStyle}>{message}</div>}

      <div style={{ ...labelStyle, lineHeight: 1.6 }}>
        更新只替换这<strong>一个 exe</strong>（下载到临时目录 → 校验 sha256 → 换掉旧文件并重启），
        不会产生需要一起分发的文件夹；配置与作品数据都不受影响。
      </div>
    </div>
  );
}
